import React, {Component} from 'react';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import Tooltip from '@material-ui/core/Tooltip';
import Preloader from '../../components/preloader';
import EmptyState from '../../components/empty-state-container';
import Auth from '../../auth/auth';
import {loadHomeSummary, homeApplyAction} from './home.actions';

const auth = new Auth();

// Module launcher tiles — icons match the ligatures in public/local-db/menu.json.
// `feature` null = not feature-gated in the app today (always shown to authed users).
const MODULES = [
  {feature: 'schema_monitoring', icon: 'live_tv', label: 'Schema Monitor', url: '/schema-monitor'},
  {feature: 'workflow', icon: 'assignment_turned_in', label: 'Workflow', url: '/workflow'},
  {feature: 'data_lineage', icon: 'call_split', label: 'Data Lineage', url: '/lineage-library'},
  {feature: 'data_dictionary_view', icon: 'menu_book', label: 'Data Dictionary', url: '/data-dictionary-view'},
];

// Status chip classes reused from the Workflow dashboard / shared chip styles.
const SOURCE_LABEL = {SCHEMA_MONITOR: 'Schema Monitor', REFRESH: 'Refresh', MANUAL: 'Manual'};

function greetingPrefix() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function relativeTime(value) {
  if (!value) return '';
  const then = new Date(value).getTime();
  if (isNaN(then)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

class Home extends Component {
  constructor(props) {
    super(props);
    const ctx = (auth.getContext && auth.getContext()) || {};
    this.userContext = ctx;
    this.scope = {instanceId: ctx.instanceId, schemaId: ctx.schemaId};
  }

  componentDidMount() {
    this.props.loadHomeSummary(this.scope);
  }

  // Whether a launcher module is visible — prefer server-provided flags,
  // fall back to the client permission cache. Ungated modules always show.
  moduleVisible = (feature) => {
    if (!feature) return true;
    const perms = (this.props.summary && this.props.summary.permissions) || {};
    let flag;
    switch (feature) {
      case 'schema_monitoring': flag = perms.canViewSchemaMonitor; break;
      case 'workflow': flag = perms.canViewWorkflow; break;
      case 'data_dictionary_view': flag = perms.canViewDictionary; break;
      case 'data_lineage': flag = perms.canViewLineage; break;
      default: flag = undefined;
    }
    if (flag !== undefined) return !!flag;
    return auth.hasPermission(feature, 'can_view');
  };

  go = (url) => this.props.history.push(url);

  handleAction = (requestId, action) => {
    this.props.homeApplyAction(requestId, action, this.scope);
  };

  renderKpis(kpis, perms) {
    const tiles = [];
    if (perms.canViewSchemaMonitor) {
      tiles.push({key: 'drifts', cls: 'amber', label: 'Schema drifts to review', value: kpis.schemaDrifts || 0, url: '/schema-monitor'});
    }
    if (perms.canViewWorkflow) {
      tiles.push({key: 'approvals', cls: 'pending', label: 'Pending approvals', value: kpis.pendingApprovals || 0, url: '/workflow'});
    }
    if (perms.canViewDictionary) {
      tiles.push({key: 'objects', cls: 'blue', label: 'Objects tracked', value: kpis.objectsTracked || 0, url: '/data-dictionary-view'});
      tiles.push({key: 'sources', cls: 'total', label: 'Connected sources', value: kpis.connectedSources || 0, url: '/data-dictionary-view'});
    }
    if (tiles.length === 0) return null;
    return (
      <div className="workflow-summary-row home-kpi-row">
        {tiles.map((t) => (
          <div className="workflow-stat-tile-wrapper" key={t.key}>
            <Tooltip title="Open" placement="top">
              <div className={`summary-stat-card ${t.cls}`} style={{cursor: 'pointer'}} onClick={() => this.go(t.url)}>
                <div className="stat-label">{t.label}</div>
                <div className="stat-value">{t.value}</div>
              </div>
            </Tooltip>
          </div>
        ))}
      </div>
    );
  }

  renderApprovals(approvals, perms) {
    return (
      <div className="home-panel">
        <div className="home-panel-head">
          <h3><span className="material-icons">assignment_turned_in</span> Needs your attention
            {approvals.length > 0 ? <span className="home-count">{approvals.length}</span> : null}
          </h3>
          <span className="home-link" onClick={() => this.go('/workflow')}>Open workflow<span className="material-icons">chevron_right</span></span>
        </div>
        {approvals.length === 0 ? (
          <EmptyState message="No pending changes need your review." />
        ) : approvals.map((a) => (
          <div className="home-approval" key={a.requestId}>
            <div className="home-approval-info">
              <div className="ent">{a.objectName || a.entityType || a.requestId}</div>
              <div className="desc">{a.diffSummary || a.changeType}</div>
              <div className="meta">
                {(SOURCE_LABEL[a.changeSource] || a.changeSource || 'Change')}
                {a.schemaName ? ` · ${a.schemaName}` : ''} {a.requestedAt ? `· ${relativeTime(a.requestedAt)}` : ''}
              </div>
            </div>
            <div className="home-approval-actions">
              <span className={`chip-${a.changeType === 'DELETE' ? 'red' : a.changeType === 'CREATE' ? 'green' : 'orange'} home-chip`}>{a.changeType}</span>
              {perms.canApprove ? (
                <Tooltip title="Approve" placement="top">
                  <button className="home-act-btn approve" disabled={this.props.isSubmittingAction}
                    onClick={() => this.handleAction(a.requestId, 'APPROVED')}>
                    <span className="material-icons">check</span>
                  </button>
                </Tooltip>
              ) : null}
              {perms.canReject ? (
                <Tooltip title="Reject" placement="top">
                  <button className="home-act-btn reject" disabled={this.props.isSubmittingAction}
                    onClick={() => this.handleAction(a.requestId, 'REJECTED')}>
                    <span className="material-icons">close</span>
                  </button>
                </Tooltip>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderActivity(activity) {
    return (
      <div className="home-panel">
        <div className="home-panel-head">
          <h3><span className="material-icons">history</span> Recent activity</h3>
        </div>
        {activity.length === 0 ? (
          <EmptyState message="No recent activity yet." />
        ) : activity.map((n) => (
          <div className="home-activity-row" key={n.id}>
            <span className="material-icons home-activity-icon">notifications</span>
            <div className="home-activity-body">
              <div className="t">{n.title}</div>
              <div className="s">{n.message}</div>
            </div>
            <span className="home-when">{relativeTime(n.createdAt)}</span>
          </div>
        ))}
      </div>
    );
  }

  renderLauncher() {
    const tiles = MODULES.filter((m) => this.moduleVisible(m.feature));
    return (
      <div className="home-panel">
        <div className="home-panel-head"><h3><span className="material-icons">apps</span> Jump back in</h3></div>
        <div className="home-modlist">
          {tiles.map((m) => (
            <div className="home-mod" key={m.url} onClick={() => this.go(m.url)}>
              <span className="home-mod-icon material-icons">{m.icon}</span>
              <div className="home-mod-label">{m.label}</div>
              <span className="material-icons home-mod-chev">chevron_right</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderSources(sources) {
    return (
      <div className="home-panel">
        <div className="home-panel-head">
          <h3><span className="material-icons">storage</span> Connected sources</h3>
        </div>
        {(!sources || sources.length === 0) ? (
          <EmptyState message="No connected sources." />
        ) : sources.map((s) => (
          <div className="home-source-row" key={s.instanceId}>
            <div className="home-source-body">
              <div className="name">{s.instanceName}</div>
              <div className="type">{s.instanceType}{s.environment ? ` · ${s.environment}` : ''}</div>
            </div>
            <span className="home-source-meta">{s.objectCount != null ? `${s.objectCount} objects` : ''}</span>
          </div>
        ))}
      </div>
    );
  }

  render() {
    const {summary, isLoading} = this.props;
    if (isLoading && !summary) {
      return (
        <div className="app-content-container">
          <div className="page-content home-dashboard"><Preloader loading={true} /></div>
        </div>
      );
    }
    const perms = (summary && summary.permissions) || {};
    const kpis = (summary && summary.kpis) || {};
    const approvals = (summary && summary.approvals) || [];
    const activity = (summary && summary.activity) || [];
    const sources = (summary && summary.sources) || [];
    const displayName = localStorage.getItem('saganUsername') || '';
    const contextName = this.userContext && this.userContext.contextName;

    return (
      <div className="app-content-container">
        <div className="page-content home-dashboard" data-testid="home-dashboard">
          <div className="home-greeting">
            <div>
              <h2>{greetingPrefix()}{displayName ? `, ${displayName}` : ''}</h2>
              <div className="home-sub">
                {contextName ? <span className="home-context-chip"><span className="material-icons">layers</span>{contextName}</span> : null}
                {' '}Here's what needs your attention today.
              </div>
            </div>
            {perms.canViewWorkflow ? (
            <div className="home-actions">
              <button className="home-btn primary" onClick={() => this.go('/workflow')}>
                <span className="material-icons">assignment_turned_in</span> Review pending changes
              </button>
            </div>
          ) : null}
          </div>

          {this.renderKpis(kpis, perms)}

          <div className="home-cols">
            <div className="home-col-main">
              {perms.canViewWorkflow ? this.renderApprovals(approvals, perms) : null}
              {perms.canViewWorkflow ? this.renderActivity(activity) : null}
            </div>
            <div className="home-col-side">
              {this.renderLauncher()}
              {perms.canViewDictionary ? this.renderSources(sources) : null}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  summary: state.homeReducer.summary,
  isLoading: state.homeReducer.isLoading,
  isSubmittingAction: state.homeReducer.isSubmittingAction,
});

export const mapDispatchToProps = (dispatch) => ({
  loadHomeSummary: (scope) => dispatch(loadHomeSummary(scope)),
  homeApplyAction: (requestId, action, scope) => dispatch(homeApplyAction(requestId, action, scope)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Home));
